import { useEffect } from 'react';

// Swiper's autoplay (hero and sponsored banner carousels, rendered through sui-carousel) keeps a
// requestAnimationFrame loop running on every frame while autoplay runs. This stops autoplay while a
// carousel is off screen and starts it again when it scrolls back into view, through Swiper's public
// instance API (`element.swiper`). Only carousels stopped here are restarted, so a carousel the user
// stopped by interacting with it stays stopped.
const CAROUSEL_SELECTOR = '.swiper';
const RESCAN_DEBOUNCE_MS = 500;

const hasAutoplay = (swiper) => !!(
  swiper && !swiper.destroyed && swiper.autoplay && swiper.params?.autoplay?.enabled
);

export const PauseOffscreenCarousels = () => {
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const observed = new WeakSet();
    const stoppedHere = new WeakSet();
    const retryTimers = new Set();
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(({ target, isIntersecting }) => {
        const { swiper } = target;
        // The `.swiper` markup is server rendered and Swiper sets `element.swiper` when it initializes,
        // which can be after the first notification. Observing it again later delivers a fresh one.
        if (!swiper) {
          observer.unobserve(target);
          if (!target.isConnected) return;
          const timer = setTimeout(() => {
            retryTimers.delete(timer);
            observer.observe(target);
          }, RESCAN_DEBOUNCE_MS);
          retryTimers.add(timer);
          return;
        }
        if (!hasAutoplay(swiper)) return;
        if (!isIntersecting && swiper.autoplay.running) {
          swiper.autoplay.stop();
          stoppedHere.add(swiper);
        } else if (isIntersecting && stoppedHere.has(swiper)) {
          stoppedHere.delete(swiper);
          swiper.autoplay.start();
        }
      });
    });

    const scan = () => {
      document.querySelectorAll(CAROUSEL_SELECTOR).forEach((element) => {
        if (observed.has(element)) return;
        observed.add(element);
        observer.observe(element);
      });
    };
    // Carousels in lazily hydrated sections mount after this effect, at any time and with or without
    // scrolling, so look again (debounced) whenever an added node is or contains a carousel. Other DOM
    // changes (timers, text updates) do not trigger a scan.
    const containsCarousel = (node) => node.nodeType === Node.ELEMENT_NODE
      && (node.matches(CAROUSEL_SELECTOR) || !!node.querySelector(CAROUSEL_SELECTOR));
    let scanTimer;
    const scheduleScan = () => {
      clearTimeout(scanTimer);
      scanTimer = setTimeout(scan, RESCAN_DEBOUNCE_MS);
    };
    const mutationObserver = typeof MutationObserver === 'undefined' ? null : new MutationObserver((records) => {
      if (records.some(({ addedNodes }) => [...addedNodes].some(containsCarousel))) scheduleScan();
    });

    scan();
    if (mutationObserver) mutationObserver.observe(document.body, { childList: true, subtree: true });
    window.addEventListener('load', scan);
    return () => {
      window.removeEventListener('load', scan);
      if (mutationObserver) mutationObserver.disconnect();
      clearTimeout(scanTimer);
      retryTimers.forEach(clearTimeout);
      observer.disconnect();
    };
  }, []);

  return null;
};
